/**
 * Toolbar  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC } from "react";
import { ClassList } from "@adapptio/lib-components";
import { Icon, ICON_SIZE } from "../Icon";

export enum ARROW_POSITION {
	LEFT = "left",
	RIGHT = "right",
	NONE = "none"
}

export enum TOOLBAR_STYLE {
	SOLID = "solid",
	TRANSPARENT = "transparent",
	STANDALONE = "standalone",
	TABS = "tabs"
}

export interface ToolbarProps {
	className?: string;
	expandable?: boolean;
	expanded?: boolean;
	style?: TOOLBAR_STYLE;
	onToggleExpanded?: () => void;
	arrowPosition?: ARROW_POSITION;
}

export const Toolbar: FC<ToolbarProps> = ({
	children,
	className,
	expanded,
	expandable,
	arrowPosition,
	style,
	onToggleExpanded,
	...props
}) => (
	<div
		className={ClassList.fromObject({
			"edt-toolbar": true,
			"edt-base": true,
			"edt-toolbar--expandable": expandable,
			"edt-toolbar--expanded": expanded,
			["edt-toolbar--style-" + (style || TOOLBAR_STYLE.SOLID)]: true,
			[className]: !!className
		}).toClassName()}
		onClick={onToggleExpanded}
		{...props}
	>
		{expandable ? (
			{
				[ARROW_POSITION.LEFT]: (
					<>
						<div className="edt-toolbar__toggle edt-toolbar__toggle--left">
							<Icon
								name={expanded ? "mdi/chevron-down" : "mdi/chevron-up"}
								size={ICON_SIZE.MEDIUM}
							/>
						</div>
						<div className="edt-toolbar__content">{children}</div>
					</>
				),
				[ARROW_POSITION.RIGHT]: (
					<>
						<div className="edt-toolbar__content">{children}</div>
						<div className="edt-toolbar__toggle edt-toolbar__toggle--right">
							<Icon
								name={expanded ? "mdi/chevron-down" : "mdi/chevron-up"}
								size={ICON_SIZE.MEDIUM}
							/>
						</div>
					</>
				),
				[ARROW_POSITION.NONE]: (
					<>
						<div className="edt-toolbar__content">{children}</div>
					</>
				)
			}[arrowPosition || ARROW_POSITION.LEFT]
		) : (
			<div className="edt-toolbar__content">{children}</div>
		)}
	</div>
);

Toolbar.defaultProps = {
	arrowPosition: ARROW_POSITION.LEFT,
	expandable: false,
	expanded: true
};
